import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTRPCAPI } from '../../../../trpc/trpc';
import type { OutreachActivityEvent, OutreachActivityPage } from '../outreachTypes';
import { useRetry } from './useRetry';

/** The events of one page of "What happened". */
const PAGE_SIZE = 30;

/**
 * `outreach.campaigns.activity` of one campaign (design outreach-v2 t6): every send, listen, reply
 * and add, newest first, one page at a time. The realtime events of the campaign read it again
 * (`useOutreachRealtime`, area `campaigns`).
 */
export function useCampaignActivity(accountId: number, campaignUuid: string) {
  const trpcAPI = useTRPCAPI();
  const query = useInfiniteQuery(
    trpcAPI.outreach.campaigns.activity.infiniteQueryOptions(
      { accountId, campaignUuid, limit: PAGE_SIZE },
      {
        getNextPageParam: (page: OutreachActivityPage | null) => page?.nextCursor ?? undefined,
        retry: false,
      }
    )
  );
  const pages = query.data?.pages as Array<OutreachActivityPage | null> | undefined;
  const events = useMemo(() => {
    const seen = new Set<string>();
    return (pages ?? [])
      .flatMap((page) => page?.events ?? [])
      .filter((event: OutreachActivityEvent) => {
        if (seen.has(event.id)) {
          return false;
        }
        seen.add(event.id);
        return true;
      });
  }, [pages]);
  const retry = useRetry(query.refetch);
  return {
    events,
    isLoading: query.isLoading,
    error: query.error,
    retry,
    hasMore: query.hasNextPage,
    loadMore: query.fetchNextPage,
    isLoadingMore: query.isFetchingNextPage,
  };
}
